import React, { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useCabinet } from "../../../context/CabinetContext";
import { useNavigate } from "react-router-dom";
import { avatarInitialColor } from "../cabinetUtils";
import { loadSettings, saveSettings } from "../../../data/gameSettings";
import {
  getAuthStatus, sendConfirmCode, verifyConfirmCode, startYandexVerify,
  changeEmail, changePassword,
} from "../../../api/auth";
import CabIcon from "../CabIcon";
import "../../../styles/cabinet-more.css";
import { openStudio } from "../../../api/playTicket";
import { openEco } from "../../../lib/ecoTrack";

// Inline-SVG иконка APK/Android для пункта «Скачать APK». Дубль такой
// же из CabinetLayout — это намеренно: вынесение в общий файл потащило
// бы кросс-компонент-зависимость ради 20 строк svg. Подсветить позже
// если появится третье место.
function DownloadApkIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 13a7 7 0 0 1 14 0" />
      <line x1="7" y1="9" x2="6" y2="7" />
      <line x1="17" y1="9" x2="18" y2="7" />
      <circle cx="9" cy="11" r="0.5" fill="currentColor" />
      <circle cx="15" cy="11" r="0.5" fill="currentColor" />
      <line x1="12" y1="15" x2="12" y2="21" />
      <polyline points="9 18 12 21 15 18" />
    </svg>
  );
}

/** Inline-SVG лампочка для пункта «Идеи Рублокса». В CabIcon её
 *  нет, выносить отдельным файлом для одной иконки overkill. */
function IdeaIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* колба */}
      <circle cx="12" cy="10" r="5" />
      {/* цоколь */}
      <rect x="9" y="15" width="6" height="2" rx="0.5" />
      <rect x="10" y="18" width="4" height="2" rx="0.5" />
      {/* лучи */}
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="4" y1="6" x2="5.5" y2="7" />
      <line x1="20" y1="6" x2="18.5" y2="7" />
    </svg>
  );
}

// Иконка «газета» для «Блог МИНа» (как в десктопном сайдбаре).
function NewsIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 4h16v16H4z" />
      <line x1="7" y1="8" x2="17" y2="8" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="7" y1="16" x2="13" y2="16" />
    </svg>
  );
}

/**
 * MoreScreen — вкладка «Ещё». Порт MoreScreen.kt, 1-в-1.
 * Профиль-шапка → меню (3 группы) → version-footer.
 * Саб-экраны Настройки / О Рублоксе / Помощь — slide-in overlay.
 *
 * props: onNavigate(tabId) — переключить вкладку кабинета (для «Битвы рейтингов»).
 */
export default function MoreScreen({ onNavigate, initialSub = null, onOpenSub }) {
  const { logout, user } = useAuth();
  const { diamonds, displayName, photo, level, profile } = useCabinet();
  const navigate = useNavigate();

  // Клик по шапке профиля → публичная страница своего профиля.
  const myId = user?.userId || profile?.id;
  const openMyProfile = () => { if (myId) navigate(`/app/profile/${myId}`); };

  const [sub, setSub] = useState(initialSub); // "settings" | "about" | "wiki" | "help" | null
  const [showLogout, setShowLogout] = useState(false);

  // Открыть подраздел: через URL (onOpenSub из CabinetPage) — даёт прямые
  // ссылки /app/settings и т.д. Фоллбэк на локальный state (совместимость).
  const goSub = (id) => { if (onOpenSub) onOpenSub(id); else setSub(id); };
  // Назад из подраздела: на корень «Ещё» (/app/more) или локально.
  const goBack = () => { if (onOpenSub) navigate("/app/more"); else setSub(null); };

  const email = profile?.email || "";
  const initial = (displayName || "?").charAt(0).toUpperCase();

  const handleLogout = () => {
    setShowLogout(false);
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="rb-cab-screen rb-more">
      {/* ── профиль-шапка (кликабельна → публичный профиль) ──────── */}
      <div
        className="rb-more__header"
        onClick={openMyProfile}
        role={myId ? "button" : undefined}
        tabIndex={myId ? 0 : undefined}
        onKeyDown={(e) => { if (myId && e.key === "Enter") openMyProfile(); }}
        style={{ cursor: myId ? "pointer" : "default" }}
        title={myId ? "Открыть мой профиль" : undefined}
      >
        {photo ? (
          <img className="rb-more__avatar" src={photo} alt={displayName} />
        ) : (
          <span
            className="rb-more__avatar"
            style={{ background: avatarInitialColor(displayName || "?") }}
          >
            {initial}
          </span>
        )}
        <div className="rb-more__header-info">
          <div className="rb-more__name">
            {displayName}
            {myId && <CabIcon name="arrowRight" size={14} />}
          </div>
          {email && <div className="rb-more__email">{email}</div>}
          {level > 0 && <span className="rb-more__level">Ур. {level}</span>}
        </div>
        <div
          className="rb-more__diamonds"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="rb-more__diamonds-val">
            <CabIcon name="diamond" size={16} />{" "}
            {diamonds == null ? "—" : diamonds}
          </div>
          <div className="rb-more__diamonds-label">Алмазы</div>
        </div>
      </div>

      {/* ── меню ───────────────────────────────────────────────── */}
      {/* Раздел «Аккаунт» перенесён в табы на странице публичного профиля
          (/app/profile/<my_id>) — там 3 вкладки: Профиль / Настройки /
          Безопасность. В этом меню «Ещё» аккаунтных пунктов больше нет. */}

      <div className="rb-more__group-label">ИГРА</div>
      <div className="rb-more__group">
        <MenuItem
          icon="swords"
          title="Битва рейтингов"
          subtitle="Ежемесячное соревнование за алмазы"
          tone="yellow"
          onClick={() => onNavigate && onNavigate("top")}
        />
        <MenuItem
          icon="cart"
          title="Магазин алмазов"
          subtitle="Открой алмазы за решение задач на школьном сайте"
          tone="cyan"
          onClick={() => openEco("minecraftia", "more_menu_diamonds")}
        />
        <MenuItem
          icon="gamepad"
          title="Создавай свои игры"
          subtitle="Как делать игры для Рублокса — бесплатный редактор в браузере"
          tone="green"
          onClick={() => goSub("wiki")}
        />
      </div>

      <div className="rb-more__group-label">ПРИЛОЖЕНИЕ</div>
      <div className="rb-more__group">
        <MenuItem
          icon={<DownloadApkIcon size={20} />}
          title="Скачать APK"
          subtitle="Нативное Android-приложение Рублокса — быстрее веб-плеера"
          tone="green"
          onClick={() => navigate("/download")}
        />
        {/* «Настройки» убраны 2026-06-16 — заглушки (графика/камера) не
            работали; убрано и на десктопе, и в мобильном меню. */}
        <MenuItem
          icon="info"
          title="О Рублоксе"
          subtitle="Что это за платформа и как она устроена"
          tone="violet"
          onClick={() => goSub("about")}
        />
        <MenuItem
          icon="flag"
          title="Помощь и поддержка"
          subtitle="Связаться со школой, FAQ, отчёт об ошибке"
          tone="red"
          onClick={() => goSub("help")}
        />
        <MenuItem
          icon="chat"
          title="Связаться с разрабом"
          subtitle="FAQ + написать МИНу. Отвечаем за 48 часов."
          tone="blue"
          onClick={() => navigate("/support")}
        />
        <MenuItem
          icon={<IdeaIcon size={20} />}
          title="Идеи Рублокса"
          subtitle="Народный роадмап: предложи свою идею, голосуй за чужие."
          tone="violet"
          onClick={() => navigate("/ideas")}
        />
        <MenuItem
          icon={<NewsIcon size={20} />}
          title="Блог МИНа"
          subtitle="Новости разработки Рублокса, обновления и анонсы"
          tone="blue"
          onClick={() => navigate("/app/news")}
        />
      </div>

      <div className="rb-more__group-label">АККАУНТ</div>
      <div className="rb-more__group">
        <MenuItem
          icon="globe"
          title="Сайт школы Майнкрафтия"
          subtitle="minecraftia-school.ru — вход общий с Рублоксом"
          tone="green"
          onClick={() => openEco("minecraftia", "more_menu_school")}
        />
        <MenuItem
          icon="globe"
          title="Соцсеть Маська"
          subtitle="маська.рф — чаты, лента, игры. Тот же аккаунт."
          tone="violet"
          onClick={() => openEco("maska", "more_menu")}
        />
        <MenuItem
          icon="exit"
          title="Выйти из аккаунта"
          subtitle="Вернуться к экрану входа. Прогресс не пропадёт."
          tone="red"
          onClick={() => setShowLogout(true)}
        />
      </div>

      {/* version-footer */}
      <div className="rb-more__footer">
        <div className="rb-more__footer-emoji">
          <CabIcon name="robot" size={28} />
        </div>
        <div className="rb-more__footer-ver">Рублокс · веб-версия</div>
        <div className="rb-more__footer-copy">© 2026 Minecraftia School</div>
      </div>

      {/* ── саб-экраны overlay ─────────────────────────────────── */}
      {sub === "account" && <AccountSub onBack={goBack} />}
      {sub === "settings" && <SettingsSub onBack={goBack} />}
      {sub === "about" && <AboutSub onBack={goBack} />}
      {sub === "wiki" && <WikiSub onBack={goBack} />}
      {sub === "help" && <HelpSub onBack={goBack} />}

      {/* ── диалог выхода ──────────────────────────────────────── */}
      {showLogout && (
        <div
          className="rb-more__dialog-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowLogout(false);
          }}
        >
          <div className="rb-more__dialog">
            <h3 className="rb-more__dialog-title">Выйти из аккаунта?</h3>
            <p className="rb-more__dialog-text">
              Тебя выкинет на экран входа. Прогресс не пропадёт — он привязан к
              аккаунту.
            </p>
            <div className="rb-more__dialog-actions">
              <button
                className="rb-more__dialog-btn rb-more__dialog-btn--ghost"
                onClick={() => setShowLogout(false)}
              >
                Отмена
              </button>
              <button
                className="rb-more__dialog-btn rb-more__dialog-btn--danger"
                onClick={handleLogout}
              >
                Выйти
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── пункт меню ────────────────────────────────────────────────── */
function MenuItem({ icon, title, subtitle, tone, onClick }) {
  // icon принимает либо string (имя для CabIcon — большинство пунктов),
  // либо готовый React-элемент (когда нужна нестандартная иконка типа
  // APK-android, для которой заводить case в CabIcon — overkill).
  const iconNode =
    typeof icon === "string" ? <CabIcon name={icon} size={20} /> : icon;
  return (
    <button className="rb-more__item" onClick={onClick}>
      <span className={`rb-more__item-ico rb-more__item-ico--${tone}`}>
        {iconNode}
      </span>
      <div className="rb-more__item-body">
        <div className="rb-more__item-title">{title}</div>
        <div className="rb-more__item-sub">{subtitle}</div>
      </div>
      <span className="rb-more__item-arrow">
        <CabIcon name="arrowRight" size={16} />
      </span>
    </button>
  );
}

/* ── scaffold саб-экрана ───────────────────────────────────────── */
function SubScaffold({ title, onBack, children }) {
  return (
    <div className="rb-more__sub">
      <div className="rb-more__sub-head">
        <button className="rb-more__sub-back" onClick={onBack}>
          <CabIcon name="back" size={16} /> Назад
        </button>
        <span className="rb-more__sub-title">{title}</span>
      </div>
      <div className="rb-more__sub-content">{children}</div>
    </div>
  );
}

/* ── саб-экран НАСТРОЙКИ ───────────────────────────────────────── */
function SettingsSub({ onBack }) {
  const [settings, setSettings] = useState(loadSettings);

  const update = (key, value) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    saveSettings(next);
  };

  return (
    <SubScaffold
      title={
        <>
          <CabIcon name="gear" size={17} /> Настройки
        </>
      }
      onBack={onBack}
    >
      <SettingsGroup icon="gamepad" title="ИГРА">
        <SliderRow
          title="Чувствительность камеры"
          subtitle="Скорость поворота в игре"
          value={settings.cameraSensitivity}
          min={1}
          max={10}
          onChange={(v) => update("cameraSensitivity", v)}
        />
        <ArrowsRow
          title="Вид камеры"
          subtitle="От третьего или первого лица"
          options={["Третье лицо", "Первое лицо"]}
          values={["third", "first"]}
          current={settings.cameraView}
          onChange={(v) => update("cameraView", v)}
        />
        <ToggleRow
          title="Инвертировать камеру"
          subtitle="Свайп вверх → камера смотрит вниз"
          checked={settings.invertCameraY}
          onChange={(v) => update("invertCameraY", v)}
        />
      </SettingsGroup>

      <SettingsGroup icon="palette" title="ГРАФИКА">
        <SliderRow
          title="Качество графики"
          subtitle="1 — слабые телефоны, 10 — флагманы"
          value={settings.graphicsQuality}
          min={1}
          max={10}
          onChange={(v) => update("graphicsQuality", v)}
        />
        <ToggleRow
          title="Показывать FPS"
          subtitle="Счётчик кадров в углу экрана"
          checked={settings.showFps}
          onChange={(v) => update("showFps", v)}
        />
        <ToggleRow
          title="Сократить анимации"
          subtitle="Отключает плавные эффекты на меню-экранах"
          checked={settings.reducedMotion}
          onChange={(v) => update("reducedMotion", v)}
        />
      </SettingsGroup>

      <SettingsGroup icon="speaker" title="ЗВУК">
        <SliderRow
          title="Громкость"
          subtitle="Эффекты, клики, чат-сигналы"
          value={settings.volume}
          min={0}
          max={10}
          onChange={(v) => update("volume", v)}
        />
        <ToggleRow
          title="Звук в чате"
          subtitle="Сигнал при новом сообщении"
          checked={settings.chatSound}
          onChange={(v) => update("chatSound", v)}
        />
      </SettingsGroup>

      <SettingsGroup icon="chat" title="ЧАТ">
        <ArrowsRow
          title="Кто может писать"
          subtitle="Кто видит твои сообщения в общем чате"
          options={["Все", "Только друзья", "Никто"]}
          values={["all", "friends", "nobody"]}
          current={settings.chatWho}
          onChange={(v) => update("chatWho", v)}
        />
        <ToggleRow
          title="Скрыть чат целиком"
          subtitle="Убрать панель чата из всех игр"
          checked={settings.disableChat}
          onChange={(v) => update("disableChat", v)}
        />
      </SettingsGroup>

      <SettingsGroup icon="lock" title="КОНФИДЕНЦИАЛЬНОСТЬ">
        <ToggleRow
          title="Показывать онлайн-статус"
          subtitle="Друзья будут видеть, что ты сейчас в Рублоксе"
          checked={settings.showOnlineStatus}
          onChange={(v) => update("showOnlineStatus", v)}
        />
        <ToggleRow
          title="Показывать текущую игру"
          subtitle="Друзья будут видеть, во что ты играешь"
          checked={settings.showCurrentGame}
          onChange={(v) => update("showCurrentGame", v)}
        />
      </SettingsGroup>

      <SettingsGroup icon="bell" title="УВЕДОМЛЕНИЯ">
        <ToggleRow
          title="Новые сообщения"
          subtitle="Когда друг написал в личку"
          checked={settings.notifyMessages}
          onChange={(v) => update("notifyMessages", v)}
        />
        <ToggleRow
          title="Достижения и рекорды"
          subtitle="Новый рекорд, попадание в топ"
          checked={settings.notifyAchievements}
          onChange={(v) => update("notifyAchievements", v)}
        />
        <ToggleRow
          title="Запросы в друзья"
          subtitle="Кто-то хочет добавить тебя в друзья"
          checked={settings.notifyFriendInvites}
          onChange={(v) => update("notifyFriendInvites", v)}
        />
        <ToggleRow
          title="Новости и новые игры"
          subtitle="Что нового на платформе"
          checked={settings.notifyNews}
          onChange={(v) => update("notifyNews", v)}
        />
        <ToggleRow
          title="Не беспокоить"
          subtitle="Полностью отключает все уведомления"
          checked={settings.doNotDisturb}
          onChange={(v) => update("doNotDisturb", v)}
        />
      </SettingsGroup>

      <SettingsGroup icon="shield" title="РОДИТЕЛЬСКИЙ КОНТРОЛЬ">
        <ArrowsRow
          title="Возрастной рейтинг игр"
          subtitle="Будут показываться только игры до этого возраста"
          options={["6+", "12+", "16+", "Все"]}
          values={["6", "12", "16", "18"]}
          current={String(settings.maxAgeRating)}
          onChange={(v) => update("maxAgeRating", parseInt(v, 10))}
        />
        <ArrowsRow
          title="Дневной лимит"
          subtitle="Сколько в день можно играть"
          options={["Без лимита", "30 мин", "1 час", "2 часа", "3 часа"]}
          values={["0", "30", "60", "120", "180"]}
          current={String(settings.dailyTimeLimitMin)}
          onChange={(v) => update("dailyTimeLimitMin", parseInt(v, 10))}
        />
      </SettingsGroup>
    </SubScaffold>
  );
}

/* ── саб-экран БЕЗОПАСНОСТЬ И ВХОД ─────────────────────────────────
 * Подтверждение email + смена email/пароля. Аккаунт общий с
 * Майнкрафтией: подтвердил здесь — подтверждён везде.
 */
/**
 * Секция безопасности и входа. Используется:
 *   1. В MoreScreen как под-страница (open=true → с шапкой SubScaffold);
 *   2. В UserProfilePage как вкладка (embedded=true → без шапки/onBack).
 * Содержит: подтверждение email (через Яндекс), смена email до подтверждения,
 * смена пароля.
 */
export function AccountSub({ onBack, embedded = false }) {
  // Стили форм — inline (cabinet-more.css не содержит нужных классов).
  const S = {
    card: {
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
      padding: 16, marginBottom: 14,
    },
    label: { fontSize: 13, fontWeight: 700, color: "#475569", marginBottom: 6 },
    input: {
      width: "100%", boxSizing: "border-box", height: 42, padding: "0 14px",
      border: "1px solid #cbd5e1", borderRadius: 10, fontSize: 14,
      outline: "none", marginBottom: 10,
    },
    btn: {
      width: "100%", height: 44, borderRadius: 10, border: "none",
      background: "linear-gradient(135deg, #3357ff 0%, #1e2da5 100%)",
      color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
    },
    btnGhost: {
      width: "100%", height: 42, borderRadius: 10,
      border: "1px solid #cbd5e1", background: "#fff", color: "#334155",
      fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: 8,
    },
    ok: {
      background: "#d1fae5", border: "1px solid #34d399", color: "#065f46",
      borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8,
    },
    err: {
      background: "#fee2e2", border: "1px solid #f87171", color: "#991b1b",
      borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8,
    },
  };

  // Статус подтверждения
  const [confirmed, setConfirmed] = useState(null);
  const [code, setCode] = useState("");
  const [codeFormOpen, setCodeFormOpen] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeMsg, setCodeMsg] = useState(null);

  // Смена email
  const [newEmail, setNewEmail] = useState("");
  const [emailPass, setEmailPass] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);

  // Смена пароля
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [passBusy, setPassBusy] = useState(false);
  const [passMsg, setPassMsg] = useState(null);

  React.useEffect(() => {
    let alive = true;
    getAuthStatus().then((s) => { if (alive) setConfirmed(s.confirmed); });
    return () => { alive = false; };
  }, []);

  const requestCode = async () => {
    setCodeMsg(null);
    setCodeBusy(true);
    const res = await sendConfirmCode();
    setCodeBusy(false);
    if (res.alreadyConfirmed) { setConfirmed(true); return; }
    if (res.ok) {
      setCodeSent(true);
      setCodeFormOpen(true);
      setCode("");
      setCodeMsg({ ok: true, text: "Код отправлен на email" });
    } else {
      setCodeFormOpen(true);
      setCodeMsg({ ok: false, text: res.message });
    }
  };

  const submitCode = async () => {
    setCodeMsg(null);
    if (!/^\d{6}$/.test(code.trim())) {
      setCodeMsg({ ok: false, text: "Код состоит из 6 цифр" });
      return;
    }
    setCodeBusy(true);
    const res = await verifyConfirmCode(code.trim());
    setCodeBusy(false);
    if (res.ok) { setConfirmed(true); setCodeFormOpen(false); }
    else setCodeMsg({ ok: false, text: res.message });
  };

  const submitEmail = async () => {
    setEmailMsg(null);
    const val = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setEmailMsg({ ok: false, text: "Некорректный email" }); return;
    }
    if (!emailPass) {
      setEmailMsg({ ok: false, text: "Введите текущий пароль" }); return;
    }
    setEmailBusy(true);
    const res = await changeEmail(emailPass, val);
    setEmailBusy(false);
    if (res.ok) {
      setEmailMsg({ ok: true, text: "Email изменён. Подтвердите новый адрес." });
      setNewEmail(""); setEmailPass("");
      setConfirmed(false);  // смена email сбрасывает подтверждение
    } else {
      setEmailMsg({ ok: false, text: res.message });
    }
  };

  const submitPass = async () => {
    setPassMsg(null);
    if (!oldPass) { setPassMsg({ ok: false, text: "Введите текущий пароль" }); return; }
    if (newPass.length < 8) {
      setPassMsg({ ok: false, text: "Новый пароль — минимум 8 символов" }); return;
    }
    if (newPass !== newPass2) {
      setPassMsg({ ok: false, text: "Пароли не совпадают" }); return;
    }
    setPassBusy(true);
    const res = await changePassword(oldPass, newPass);
    setPassBusy(false);
    if (res.ok) {
      setPassMsg({ ok: true, text: "Пароль изменён" });
      setOldPass(""); setNewPass(""); setNewPass2("");
    } else {
      setPassMsg({ ok: false, text: res.message });
    }
  };

  const inner = (
    <>
      {/* Подтверждение email */}
      <div style={S.card}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>
          Подтверждение email
        </div>
        {confirmed === null && (
          <div style={{ color: "#94a3b8", fontSize: 13 }}>Загрузка…</div>
        )}
        {confirmed === true && (
          <div style={S.ok}>Аккаунт подтверждён — все функции доступны</div>
        )}
        {confirmed === false && (
          <>
            <div style={{ fontSize: 14, color: "#3D4148", marginBottom: 12, lineHeight: 1.5 }}>
              Подтверди email, чтобы открыть комментарии, чат и публикацию
              игр. Самый простой способ — войти через Яндекс.
            </div>
            {codeMsg && (
              <div style={codeMsg.ok ? S.ok : S.err}>{codeMsg.text}</div>
            )}
            <button
              type="button"
              onClick={async () => {
                setCodeMsg(null);
                setCodeBusy(true);
                const res = await startYandexVerify();
                if (res.ok && res.authorizeUrl) {
                  localStorage.setItem("yandex_oauth_mode", "verify");
                  window.location.href = res.authorizeUrl;
                } else {
                  setCodeMsg({ ok: false, text: res.message || "Не удалось начать подтверждение" });
                  setCodeBusy(false);
                }
              }}
              disabled={codeBusy}
              style={{
                width: "100%", height: 46,
                background: "#000", color: "#fff",
                border: "none", borderRadius: 10,
                fontSize: 15, fontWeight: 700,
                cursor: codeBusy ? "not-allowed" : "pointer",
                opacity: codeBusy ? 0.6 : 1,
                display: "flex", alignItems: "center",
                justifyContent: "center", gap: 10,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="11" fill="#FC3F1D" />
                <path d="M13.3 18.5h2.05V5h-3.05c-2.85 0-4.6 1.5-4.6 4 0 1.8.9 2.95 2.45 3.7L7.5 18.5h2.2l2.85-6h.75v6zm-1.05-7.85h-.8c-1.5 0-2.3-.85-2.3-2.3 0-1.5.8-2.2 2.3-2.2h.8v4.5z" fill="#fff" />
              </svg>
              {codeBusy ? "Открываем Яндекс..." : "Подтвердить через Яндекс"}
            </button>

            <div style={{
              marginTop: 12, fontSize: 13,
              color: "#60646A", textAlign: "center", lineHeight: 1.5,
            }}>
              Нет Яндекс-аккаунта?{" "}
              <span
                title="Сервис временно недоступен"
                style={{
                  color: "#9CA0A8",
                  textDecoration: "underline",
                  cursor: "not-allowed",
                }}
              >
                Получить код на email
              </span>
              <div style={{ fontSize: 12, color: "#9CA0A8", marginTop: 4 }}>
                (откроется в течение дня — настраивается)
              </div>
            </div>
          </>
        )}
      </div>

      {/* Смена email — только до подтверждения. После — через поддержку.
          2026-05-26: защита от угона аккаунта. */}
      {confirmed === false && (
        <div style={S.card}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>
            Сменить email
          </div>
          <div style={{ fontSize: 12, color: "#60646A", marginBottom: 12, lineHeight: 1.5 }}>
            Сменить email можно пока он не подтверждён. После подтверждения
            email закрепляется за аккаунтом.
          </div>
          <div style={S.label}>Новый email</div>
          <input style={S.input} type="email" value={newEmail}
                 onChange={(e) => setNewEmail(e.target.value)}
                 placeholder="new@example.com" />
          <div style={S.label}>Текущий пароль</div>
          <input style={S.input} type="password" value={emailPass}
                 onChange={(e) => setEmailPass(e.target.value)}
                 placeholder="Текущий пароль" />
          {emailMsg && (
            <div style={emailMsg.ok ? S.ok : S.err}>{emailMsg.text}</div>
          )}
          <button style={{ ...S.btn, marginTop: 4 }}
                  onClick={submitEmail} disabled={emailBusy}>
            {emailBusy ? "Сохранение…" : "Сохранить email"}
          </button>
        </div>
      )}
      {confirmed === true && (
        <div style={S.card}>
          <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 8 }}>
            Email закреплён за аккаунтом
          </div>
          <div style={{ fontSize: 13, color: "#60646A", lineHeight: 1.5 }}>
            Изменение email возможно через поддержку:{" "}
            <a href="mailto:maksimivankov26@yandex.ru" style={{ color: "#3357FF" }}>
              maksimivankov26@yandex.ru
            </a>
          </div>
        </div>
      )}

      {/* Смена пароля */}
      <div style={S.card}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>
          Сменить пароль
        </div>
        <div style={S.label}>Текущий пароль</div>
        <input style={S.input} type="password" value={oldPass}
               onChange={(e) => setOldPass(e.target.value)}
               placeholder="Текущий пароль" />
        <div style={S.label}>Новый пароль</div>
        <input style={S.input} type="password" value={newPass}
               onChange={(e) => setNewPass(e.target.value)}
               placeholder="Минимум 8 символов" />
        <div style={S.label}>Повторите новый пароль</div>
        <input style={S.input} type="password" value={newPass2}
               onChange={(e) => setNewPass2(e.target.value)}
               placeholder="Ещё раз новый пароль" />
        {passMsg && (
          <div style={passMsg.ok ? S.ok : S.err}>{passMsg.text}</div>
        )}
        <button style={{ ...S.btn, marginTop: 4 }}
                onClick={submitPass} disabled={passBusy}>
          {passBusy ? "Сохранение…" : "Сохранить пароль"}
        </button>
      </div>
    </>
  );

  // Если embedded — рендерим без шапки SubScaffold (для UserProfilePage).
  // Иначе — оборачиваем как раньше (для MoreScreen sub-page).
  if (embedded) return <div className="rb-up__security">{inner}</div>;
  return (
    <SubScaffold
      title={<><CabIcon name="shield" size={17} /> Безопасность и вход</>}
      onBack={onBack}
    >
      {inner}
    </SubScaffold>
  );
}

function SettingsGroup({ icon, title, children }) {
  return (
    <>
      <div className="rb-more__sg-label">
        <CabIcon name={icon} size={14} /> {title}
      </div>
      <div className="rb-more__sg">{children}</div>
    </>
  );
}

function ToggleRow({ title, subtitle, checked, onChange }) {
  return (
    <div className="rb-more__row" onClick={() => onChange(!checked)}>
      <div className="rb-more__row-body">
        <div className="rb-more__row-title">{title}</div>
        <div className="rb-more__row-sub">{subtitle}</div>
      </div>
      <span className={`rb-more__toggle ${checked ? "is-on" : ""}`}>
        <span className="rb-more__toggle-knob" />
      </span>
    </div>
  );
}

function SliderRow({ title, subtitle, value, min, max, onChange }) {
  const dots = [];
  for (let i = min; i <= max; i++) dots.push(i);
  return (
    <div className="rb-more__slider-row">
      <div className="rb-more__slider-head">
        <div className="rb-more__row-body">
          <div className="rb-more__row-title">{title}</div>
          <div className="rb-more__row-sub">{subtitle}</div>
        </div>
        <span className="rb-more__slider-val">{value}</span>
      </div>
      <div className="rb-more__slider-dots">
        {dots.map((i) => (
          <button
            key={i}
            className={`rb-more__dot ${i <= value ? "is-active" : ""}`}
            onClick={() => onChange(i)}
          >
            {i}
          </button>
        ))}
      </div>
    </div>
  );
}

function ArrowsRow({ title, subtitle, options, values, current, onChange }) {
  const idx = Math.max(values.indexOf(current), 0);
  return (
    <div className="rb-more__row">
      <div className="rb-more__row-body">
        <div className="rb-more__row-title">{title}</div>
        <div className="rb-more__row-sub">{subtitle}</div>
      </div>
      <div className="rb-more__arrows">
        <button
          className="rb-more__arrow"
          onClick={() => onChange(values[(idx - 1 + values.length) % values.length])}
        >
          ‹
        </button>
        <span className="rb-more__arrows-val">{options[idx]}</span>
        <button
          className="rb-more__arrow"
          onClick={() => onChange(values[(idx + 1) % values.length])}
        >
          ›
        </button>
      </div>
    </div>
  );
}

/* ── саб-экран О РУБЛОКСЕ ──────────────────────────────────────── */
function AboutSub({ onBack }) {
  return (
    <SubScaffold
      title={
        <>
          <CabIcon name="info" size={17} /> О Рублоксе
        </>
      }
      onBack={onBack}
    >
      <InfoCard
        icon="gamepad"
        title="Что такое Рублокс?"
        body="Платформа для 3D-игр от учеников Майнкрафтии. Играй в десятки игр от других учеников или создавай свои в редакторе на сайте школы. Делись с друзьями, попадай в лидерборды, открывай скины."
      />
      <InfoCard
        icon="school"
        title="Школа Майнкрафтия"
        body="Аккаунт работает и в Рублоксе, и на minecraftia-school.ru. Решая задачи на сайте, ты получаешь алмазы — внутреннюю валюту Рублокса. На сайте больше 1000 бесплатных задач по Python, JavaScript и блочному программированию."
        link={{ text: "Перейти на сайт школы", url: "https://minecraftia-school.ru" }}
      />
      <InfoCard
        icon="diamond"
        title="Алмазы и магазин"
        body="За алмазы в Рублоксе можно покупать скины, аксессуары, эмоции и другие косметические штуки. Зарабатывать алмазы проще всего на сайте школы — за каждую решённую задачу автоматически зачисляются алмазы."
      />
      <InfoCard
        icon="trophy"
        title="Битва рейтингов"
        body="Каждый месяц 1-го числа в 00:00 рейтинг обнуляется. Топ-3 получают алмазы автоматически: 100 / 50 / 20. Рейтинг копится за решение задач и попадание в лидерборды игр."
      />
      <InfoCard
        icon="users"
        title="Друзья"
        body="Добавляй других учеников Майнкрафтии в друзья, общайся в личных чатах и заходи в одну игру вместе."
      />
      <InfoCard
        icon="scroll"
        title="Документы"
        body="Пользовательское соглашение и политика обработки персональных данных."
        link={{ text: "Открыть соглашение", url: "/terms" }}
      />
    </SubScaffold>
  );
}

/* ── саб-экран СОЗДАВАЙ СВОИ ИГРЫ (облегчённая вика) ───────────── */
function WikiSub({ onBack }) {
  return (
    <SubScaffold
      title={
        <>
          <CabIcon name="gamepad" size={17} /> Создавай свои игры
        </>
      }
      onBack={onBack}
    >
      {/* крупный блок-призыв */}
      <div className="rb-more__wiki-hero">
        <div className="rb-more__wiki-hero-emoji">
          <CabIcon name="rocket" size={34} />
        </div>
        <div className="rb-more__wiki-hero-title">
          Любую игру в Рублоксе создал такой же ученик
        </div>
        <p className="rb-more__wiki-hero-text">
          В Рублоксе можно не только играть, но и собирать собственные 3D-игры —
          платформеры, гонки, стрелялки, выживалки. Не нужно ничего скачивать и
          уметь программировать заранее: редактор работает прямо в браузере и
          всему научит по шагам.
        </p>
        <button
          type="button"
          className="rb-more__wiki-hero-btn"
          onClick={() => openStudio()}
          style={{ border: "none", cursor: "pointer", font: "inherit" }}
        >
          <CabIcon name="gamepad" size={16} /> Открыть редактор игр
        </button>
      </div>

      <InfoCard
        icon="info"
        title="Где находится редактор"
        body="Редактор игр Рублокса открывается прямо в браузере на studio.rublox.pro — это бесплатный 3D-редактор. Аккаунт — тот же, что и в Рублоксе: отдельной регистрации не нужно, кнопка ниже сама подхватит твою авторизацию."
        link={{
          text: "Перейти в редактор",
          onClick: () => openStudio(),
        }}
      />
      <InfoCard
        icon="puzzle"
        title="Из чего собирается игра"
        body="Сцену строишь из блоков, готовых 3D-моделей и простых фигур — кубов, сфер, цилиндров. Можно лепить ландшафт с холмами и пещерами, ставить точку появления игрока, лампы, частицы и триггеры. Всё двигается, вращается и красится в любой цвет."
      />
      <InfoCard
        icon="book"
        title="Скрипты оживляют игру"
        body="Чтобы монетка собиралась, дверь открывалась, а враг гнался за тобой — нужны скрипты. Это короткие команды на простом языке. Редактор показывает примеры и подсказывает: первый скрипт получится за пару минут."
      />
      <InfoCard
        icon="palette"
        title="Интерфейс игры"
        body="Поверх 3D-сцены можно нарисовать кнопки, надписи, счётчики очков и полоски здоровья — как в настоящих играх. Всё перетаскивается мышкой в визуальном редакторе UI."
      />
      <InfoCard
        icon="rocket"
        title="Опубликуй и поделись"
        body="Готовую игру можно сохранить и опубликовать — она появится в Рублоксе, и в неё смогут играть другие ученики. За популярные игры начисляются алмазы."
      />
      <InfoCard
        icon="school"
        title="Полная вики и 50 игр-уроков"
        body="На сайте школы есть подробная вики редактора: описание каждой механики со скриншотами и 50 готовых мини-игр-уроков от простого к сложному — собери монетки, паркур, тир, гонки, целые RPG. Открывай и собирай по шагам."
        link={{
          text: "Открыть вики редактора",
          onClick: () => openStudio({ subpath: "/docs" }),
        }}
      />
    </SubScaffold>
  );
}

/* ── саб-экран ПОМОЩЬ ──────────────────────────────────────────── */
function HelpSub({ onBack }) {
  return (
    <SubScaffold
      title={
        <>
          <CabIcon name="flag" size={17} /> Помощь
        </>
      }
      onBack={onBack}
    >
      <InfoCard
        icon="school"
        title="Связаться со школой"
        body="Если что-то не работает или есть идеи — напиши нам. Мы — школа программирования Майнкрафтия, отвечаем быстро."
        link={{ text: "Написать на сайте", url: "https://minecraftia-school.ru" }}
      />
      <InfoCard
        icon="chat"
        title="Telegram-канал"
        body="Новости, новые игры, обновления плеера. Подписывайся чтобы не пропустить новые скины и события Битвы рейтингов."
        link={{ text: "Открыть канал", url: "https://t.me/minecraftia_school" }}
      />
      <InfoCard
        icon="warning"
        title="Нашёл баг?"
        body="В каждой игре и в редакторе есть кнопка отправки баг-репорта — опиши что не так. Это самый быстрый способ сообщить разработчикам."
      />
      <InfoCard
        icon="question"
        title="Часто задаваемые вопросы"
        body="Как заработать алмазы? Как добавить друга? Что такое Битва рейтингов? Все ответы — на сайте школы в разделе FAQ."
        link={{ text: "Открыть FAQ", url: "https://minecraftia-school.ru/faq" }}
      />
    </SubScaffold>
  );
}

function InfoCard({ icon, title, body, link }) {
  return (
    <div className="rb-more__info">
      <div className="rb-more__info-head">
        <span className="rb-more__info-emoji">
          <CabIcon name={icon} size={20} />
        </span>
        <span className="rb-more__info-title">{title}</span>
      </div>
      <p className="rb-more__info-body">{body}</p>
      {link && (
        link.onClick ? (
          <button
            type="button"
            className="rb-more__info-link"
            onClick={link.onClick}
            style={{
              background: "none", border: "none", padding: 0,
              font: "inherit", cursor: "pointer", color: "inherit",
            }}
          >
            <CabIcon name="arrowRight" size={14} /> {link.text}
          </button>
        ) : (
          <a
            className="rb-more__info-link"
            href={link.url}
            target={link.url && link.url.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
          >
            <CabIcon name="arrowRight" size={14} /> {link.text}
          </a>
        )
      )}
    </div>
  );
}
